import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { forkJoin, Observable, of, Subscription } from '../../../../node_modules/rxjs';
import { Category, ICategoryList, Lookup, LookupCategory } from '../models/lookup-category';
import { SubjectType } from '../models/subject-type';
import { BaseRestService } from './base-rest.service';
import { SubjectTypeService } from './subject-type.service';
import { RelationshipService } from './relationship.service';
import { LookupCategoryService } from './lookup-category.service';
import { UserService } from './user.service';
import { User } from '../models/user';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class CategoryService extends BaseRestService<Category, ICategoryList> implements OnDestroy {

  private onRelationshipChanged: Subscription;
  private onSubjectTypeChanged: Subscription;
  private onLookupCategoriesChanged: Subscription;
  private changed: Subscription;

  constructor(
    http: HttpClient,
    private subjectTypeService: SubjectTypeService,
    private relationshipService: RelationshipService,
    private lookupCategoryService: LookupCategoryService,
    private userService: UserService,
    router: Router) {
    super(CategoryService, http, 'api/categories', 'category', router);

    // enable caching
    this.cacheEnabled = true;

    this.onRelationshipChanged = relationshipService.onItemChanged.subscribe((relationship) => {
      const r = relationship;
      const cache = this.getCache();
      const hydrate$ = [];
      cache.filter((i) => (',' + i.primaryRelationshipId + ',').includes(',' + r.id + ',')).forEach((c) => {
        c.primaryRelationship = undefined;
        hydrate$.push(this.hydrate(c));
      });
      if (hydrate$.length > 0) {
        forkJoin(hydrate$).pipe(take(1)).subscribe();
      }
    });

    this.onSubjectTypeChanged = subjectTypeService.onItemChanged.subscribe((subjectType) => {
      const st = subjectType;
      const cache = this.getCache();
      const hydrate$ = [];
      cache.filter((i) => i.subjectTypeId === st.id).forEach((c) => {
        c.subjectType = undefined;
        hydrate$.push(this.hydrate(c));
      });
      if (hydrate$.length > 0) {
        forkJoin(hydrate$).pipe(take(1)).subscribe();
      }
    });

    this.onLookupCategoriesChanged = this.lookupCategoryService.onChanged.subscribe((lookupCategories: LookupCategory[]) => {
      const lc = lookupCategories;
      const cache = this.getCache();
      const hydrate$ = [];
      cache.forEach((c) => {
        c.lookupCategories = lc.filter((x) => x.categoryId === c.id);
        hydrate$.push(this.hydrate(c));
      });
      if (hydrate$.length > 0) {
        forkJoin(hydrate$).pipe(take(1)).subscribe();
      }
    });

    this.changed = this.onChanged.subscribe((categories) => {
      this.lookupCategoryService.categories = [...categories];
    });
  }

  ngOnDestroy() {
    this.onRelationshipChanged?.unsubscribe();
    this.onSubjectTypeChanged?.unsubscribe();
    this.onLookupCategoriesChanged?.unsubscribe();
    this.changed?.unsubscribe();
  }

  getAll() {
    this.subjectTypeService.getAllData().pipe(take(1)).subscribe((_) => {
      super.getAll();
    });
  }
  getCategoryByName(category: string): Observable<Category> {
    return this.getAllData().pipe(take(1), map((categories) => {
      return categories.find((x) => x.description.toLowerCase() === category.toLowerCase());
    }));
  }

  getDescription(id: number): string {
    const cache = this.getCache();
    const category = cache.find((x) => (x.id === id));
    return (category) ? category.description : id.toString();
  }

  getCategoriesBySubjectType(subjectTypeId: number | undefined): Observable<Category[]> {
    return this.getAllData().pipe(take(1), map((categories) => {
      if (subjectTypeId) {
        return categories.filter((x) => (x.subjectTypeId === subjectTypeId) && (!x.hideRecord));
      }
      else {
        return categories.filter((x) => (!x.hideRecord));
      }
    }));
  }

  save(entity: Category, successMessage?: string) {
    if (entity) {
      // make sure lookupCategory objects are not set before save...
      entity.lookupCategories.map((i) => {
        i.category = null;
        i.lookup = null;
        return i;
      });

      entity.categoryUserSecurity.forEach((i) => {
        if (i.userId){
          (i as any).user = null;
        }
      });
    }
    super.save(entity, successMessage);
  }

  hydrate(item: Category): Observable<Category> {    
    const hydrate$ = [];
    if ((!item.subjectType) || (item.subjectType.id !== item.subjectTypeId)) {
      hydrate$.push(this.subjectTypeService.getById(item.subjectTypeId).pipe(map((subjectType) => {
        item.subjectType = new SubjectType(subjectType);
        return item;
      })));
    }

    if (item.lookupCategories && (item.lookupCategories.length > 0)) {
      item.lookupCategories.map((i) => {
        if ((!i.lookup) || (i.lookup.id !== i.lookupId)) {
          hydrate$.push(this.lookupCategoryService.getLookupById(i.lookupId).pipe(take(1), map((lookup: Lookup) => {
            i.lookup = lookup;
            return item;
          })));
        }
      });
    }

    hydrate$.push(of(item));
    return forkJoin(hydrate$).pipe(take(1), map((_) => {
      item.lookups = this.getLookupsAsString(item);
      this.getUsersAsString(item).subscribe((s) => item.restrictedUsers = s);
      item.primaryRelationship = this.getRelationshipsAsString(item);
      return item;
    }));
  }

  getLookupsAsString(item: Category): string {
    return (item.lookupCategories) ? item.lookupCategories
      .filter((i) => i.lookup)
      .sort((a, b) => a.lookup.description.localeCompare(b.lookup.description))
      .map((i) => i.lookup.description).join(', ') : '';
  }

  getUsersAsString(item: Category): Observable<string> {
    item.categoryUserSecurity = item.categoryUserSecurity || [];
    if (item && item.autoRestrict && (item.categoryUserSecurity.length > 0)) {
      return this.userService.getActiveUsers().pipe(take(1), map((users: User[]) => {
        const selectedUsers = item.categoryUserSecurity.map((i) => i.userId);
        return [...users.filter((i) => selectedUsers.indexOf(i.id) > -1)].map((i) => i.fullName).join(', ');
      }));
    }
    else {
      return of('');
    }
  }

  getRelationshipsAsString(item: Category) {
    const primaryRelationships = [];
    const primaryRelationshipIds = (item.primaryRelationshipId || '').split(',');
    const relationships = this.relationshipService.getCache();
    primaryRelationshipIds.forEach((i) => {
      const id = !isNaN(Number(i)) ? Number(i) : 0;
      const relationship = relationships.find((x) => x.id === id);
      if (relationship) {
        primaryRelationships.push(relationship.name);
      }
    });
    return primaryRelationships.join(',');
  }
}
